/**
 * `records` capability (sync Phase 7): the ONLY sanctioned data path for
 * extension records. Backed by the extension_documents table in the sync
 * store, so everything written here rides the same local → SQLite →
 * cloud/team path as core entities (field-level outbox ops, tombstones,
 * per-record LWW).
 *
 * `extensionId` is injected by the host from the RPC envelope (same rule as
 * db.js) — an extension can never read or write another extension's records,
 * and nothing here exposes raw SQL. Records carry `scope_id` in data to bind
 * children to their root document (e.g. a board) for bulk scope syncs.
 */
import { invoke } from '@tauri-apps/api/core';

async function call(cmd, args) {
  return invoke(cmd, args);
}

export const recordsCapability = {
  capability: 'records',
  methods: {
    /** records.list({ workspaceId, recordType?, scopeId? }) */
    'records.list': (extensionId, { workspaceId, recordType, scopeId }) =>
      call('extension_records_list', {
        workspaceId: String(workspaceId),
        extensionId,
        recordType: recordType || null,
        scopeId: scopeId || null,
      }),

    /** records.upsert({ workspaceId, record: {id, record_type, parent_id?, sort_key?, data} }) */
    'records.upsert': (extensionId, { workspaceId, record }) =>
      call('extension_records_upsert', {
        workspaceId: String(workspaceId),
        extensionId,
        record,
      }),

    /** records.delete({ workspaceId, recordId }) — tombstone, never hard-delete. */
    'records.delete': (extensionId, { workspaceId, recordId }) =>
      call('extension_records_delete', {
        workspaceId: String(workspaceId),
        extensionId,
        recordId: String(recordId),
      }),

    /**
     * records.syncScope({ workspaceId, scopeId, records }) — bulk diff of one
     * root document (board): upserts changes, tombstones missing members.
     */
    'records.syncScope': (extensionId, { workspaceId, scopeId, records }) =>
      call('extension_records_sync_scope', {
        workspaceId: String(workspaceId),
        extensionId,
        scopeId: String(scopeId),
        records: records || [],
      }),
  },
};
